import { fmtNum } from '../utils/format';

// Óvocitos por sesión (view=trends → trends.oocytes_per_session). Dos curvas
// SUAVES (spline Catmull-Rom) superpuestas: Viables (teal, prominente) y Total
// (sky, tenue y punteada), cada una con área. El contrato manda las últimas ≤9
// por fecha desc → se invierte a orden cronológico (izq→der).

const W = 320, H = 150, PAD_X = 14, PAD_T = 14, PAD_B = 24;
const TOTAL_COLOR = '#3bb5ed';
const VIABLES_COLOR = '#04c2a0';

// Catmull-Rom → cúbicas de Bézier (curva suave que pasa por todos los puntos).
function smoothLine(pts) {
    if (pts.length < 2) return '';
    let d = `M ${pts[0].x},${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`;
    }
    return d;
}

export default function OocytesChart({ data }) {
    const series = [...(data || [])].reverse();
    const n = series.length;
    const innerW = W - PAD_X * 2;
    const innerH = H - PAD_T - PAD_B;
    const max = Math.max(1, ...series.map(s => Number(s.total) || 0));
    const x = (i) => (n <= 1 ? W / 2 : PAD_X + (i * innerW) / (n - 1));
    const y = (v) => PAD_T + innerH - ((Number(v) || 0) / max) * innerH;
    const baseline = PAD_T + innerH;

    const totalPts = series.map((s, i) => ({ x: x(i), y: y(s.total) }));
    const viablesPts = series.map((s, i) => ({ x: x(i), y: y(s.viables) }));
    const area = (pts) => (n < 2 ? '' : `${smoothLine(pts)} L ${x(n - 1)},${baseline} L ${x(0)},${baseline} Z`);

    return (
        <article className="report-card report-trend">
            <h2 className="report-section__title">Óvocitos por sesión</h2>

            {!n ? (
                <p className="report-empty">Sin sesiones recientes.</p>
            ) : (
                <>
                    <svg className="report-trend__chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Óvocitos por sesión">
                        <line x1={PAD_X} y1={baseline} x2={W - PAD_X} y2={baseline} stroke="var(--color-border)" strokeWidth="1" />
                        {n >= 2 && <path d={area(totalPts)} fill={TOTAL_COLOR} fillOpacity="0.05" />}
                        {n >= 2 && <path d={area(viablesPts)} fill={VIABLES_COLOR} fillOpacity="0.18" />}
                        {n >= 2 && <path d={smoothLine(totalPts)} fill="none" stroke={TOTAL_COLOR} strokeWidth="1.5" strokeOpacity="0.5" strokeDasharray="4 3" strokeLinecap="round" />}
                        {n >= 2 && <path d={smoothLine(viablesPts)} fill="none" stroke={VIABLES_COLOR} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />}
                        {series.map((s, i) => (
                            <g key={s.session_id}>
                                <circle cx={x(i)} cy={y(s.total)} r="2" fill={TOTAL_COLOR} fillOpacity="0.55"><title>{`${s.session_code}: ${fmtNum(s.total)} óvocitos`}</title></circle>
                                <circle cx={x(i)} cy={y(s.viables)} r="2.8" fill={VIABLES_COLOR}><title>{`${s.session_code}: ${fmtNum(s.viables)} viables`}</title></circle>
                            </g>
                        ))}
                    </svg>
                    <div className="report-trend__legend">
                        <span className="report-trend__key"><span className="report-trend__dot report-trend__dot--dashed" style={{ color: TOTAL_COLOR }} />Total</span>
                        <span className="report-trend__key"><span className="report-trend__dot" style={{ background: VIABLES_COLOR }} />Viables</span>
                        <span className="report-trend__caption">Últimas {n} sesiones</span>
                    </div>
                </>
            )}
        </article>
    );
}
